'use client'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { SectionConfig } from '@/types/site'
import type { FAQContent, FAQItem } from '@/types/sections'
import { getTitleSizeClass, getTextAlignClass } from '../_utils'
import { ChevronDown } from 'lucide-react'
import { elementProps } from '@/lib/elementHelpers'
import GrandTitle from '@/components/GrandTitle'

interface FAQSectionProps {
  config: SectionConfig
  isEditing?: boolean
}

// ═══════════════════════════════════════════════════
// Accordion Items per universe
// ═══════════════════════════════════════════════════

function StartupAccordionItem({ item, accent, sectionId, itemIndex }: { item: FAQItem; accent: string; sectionId: string; itemIndex: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-zinc-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span {...elementProps(sectionId, `items.${itemIndex}.question`, 'heading')} className="font-medium text-sm text-zinc-900">{item.question}</span>
        <ChevronDown
          className={cn('w-4 h-4 shrink-0 transition-transform duration-200', open && 'rotate-180')}
          style={{ color: accent }}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-200',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <p {...elementProps(sectionId, `items.${itemIndex}.answer`, 'text')} className="pb-5 text-sm leading-relaxed text-zinc-500">{item.answer}</p>
        </div>
      </div>
    </div>
  )
}

function CorporateAccordionItem({ item, accent, sectionId, itemIndex }: { item: FAQItem; accent: string; sectionId: string; itemIndex: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-slate-700/50 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
      >
        <span {...elementProps(sectionId, `items.${itemIndex}.question`, 'heading')} className="font-medium text-sm text-brand-ink">{item.question}</span>
        <ChevronDown
          className={cn('w-4 h-4 shrink-0 transition-transform duration-200', open && 'rotate-180')}
          style={{ color: accent }}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-200',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <p {...elementProps(sectionId, `items.${itemIndex}.answer`, 'text')} className="pb-5 text-sm leading-relaxed text-slate-400">{item.answer}</p>
        </div>
      </div>
    </div>
  )
}

function LuxeAccordionItem({ item, accent, sectionId, itemIndex }: { item: FAQItem; accent: string; sectionId: string; itemIndex: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-zinc-100/60 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left gap-4"
      >
        <span {...elementProps(sectionId, `items.${itemIndex}.question`, 'heading')} className="font-normal text-sm tracking-wide text-zinc-800">{item.question}</span>
        <span
          className="text-lg font-normal shrink-0 select-none leading-none w-5 text-center transition-transform duration-200"
          style={{ color: accent }}
        >
          {open ? '\u2212' : '+'}
        </span>
      </button>
      <div
        className={cn(
          'grid transition-all duration-200',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <p {...elementProps(sectionId, `items.${itemIndex}.answer`, 'text')} className="pb-6 text-sm font-normal leading-relaxed text-zinc-500 tracking-wide">{item.answer}</p>
        </div>
      </div>
    </div>
  )
}

function CreativeAccordionItem({ item, sectionId, itemIndex }: { item: FAQItem; sectionId: string; itemIndex: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={cn('border-2 border-zinc-900 p-0', open ? 'bg-yellow-50' : 'bg-transparent')}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 px-5 text-left gap-4"
      >
        <span {...elementProps(sectionId, `items.${itemIndex}.question`, 'heading')} className="font-black text-sm text-zinc-900 uppercase tracking-tight">{item.question}</span>
        <ChevronDown
          className={cn('w-5 h-5 shrink-0 transition-transform duration-200 text-zinc-900', open && 'rotate-180')}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-200',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <p {...elementProps(sectionId, `items.${itemIndex}.answer`, 'text')} className="pb-4 px-5 text-sm leading-relaxed text-zinc-700">{item.answer}</p>
        </div>
      </div>
    </div>
  )
}

function EcommerceAccordionItem({ item, accent, sectionId, itemIndex }: { item: FAQItem; accent: string; sectionId: string; itemIndex: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div
      className={cn(
        'rounded-xl border transition-colors duration-200',
        open ? 'bg-emerald-50/50 border-emerald-100' : 'bg-white border-zinc-100'
      )}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 px-5 text-left gap-4"
      >
        <span {...elementProps(sectionId, `items.${itemIndex}.question`, 'heading')} className="font-medium text-sm text-zinc-900">{item.question}</span>
        <ChevronDown
          className={cn('w-4 h-4 shrink-0 transition-transform duration-200', open && 'rotate-180')}
          style={{ color: accent }}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-200',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <p {...elementProps(sectionId, `items.${itemIndex}.answer`, 'text')} className="pb-4 px-5 text-sm leading-relaxed text-zinc-500">{item.answer}</p>
        </div>
      </div>
    </div>
  )
}

function BrixsaAccordionItem({ item, sectionId, itemIndex }: { item: FAQItem; sectionId: string; itemIndex: number }) {
  const [open, setOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
      <div
        role="button"
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 0',
          textAlign: 'left',
          gap: '16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <span
          {...elementProps(sectionId, `items.${itemIndex}.question`, 'heading')}
          style={{
            fontFamily: "var(--font-heading, inherit)",
            fontSize: 'clamp(16px, 3vw, 20px)',
            fontWeight: 500,
            color: hovered ? 'var(--color-accent, #c8a97e)' : 'var(--color-background, #e1e1e1)',
            transition: 'color 0.2s ease',
            lineHeight: 1.4,
          }}
        >
          {item.question}
        </span>
        <span
          style={{
            fontSize: '24px',
            fontWeight: 400,
            flexShrink: 0,
            width: '28px',
            textAlign: 'center',
            lineHeight: 1,
            userSelect: 'none',
            color: hovered ? 'var(--color-accent, #c8a97e)' : 'var(--color-background, #e1e1e1)',
            transition: 'color 0.2s ease',
          }}
        >
          {open ? '\u2212' : '+'}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          opacity: open ? 1 : 0,
          transition: 'grid-template-rows 0.2s ease, opacity 0.2s ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <p
            {...elementProps(sectionId, `items.${itemIndex}.answer`, 'text')}
            style={{
              fontFamily: "var(--font-body, inherit)",
              fontSize: '15px',
              lineHeight: 1.7,
              color: 'color-mix(in srgb, var(--color-background, #e1e1e1) 60%, transparent)',
              paddingBottom: '24px',
              margin: 0,
            }}
          >
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  )
}

function GlassAccordionItem({ item, accent, sectionId, itemIndex }: { item: FAQItem; accent: string; sectionId: string; itemIndex: number }) {
  const [open, setOpen] = useState(false)
  const num = String(itemIndex + 1).padStart(2, '0')

  // Render answer line by line so bullet "•" can be colored gold
  const lines = item.answer.split('\n')

  return (
    <div
      className="last:border-b-0 transition-colors duration-300 hover:bg-[color-mix(in srgb, var(--color-brand-accent) 2%, transparent)]"
      style={{ borderBottom: `1px solid ${accent}2e` }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="gfaq-row w-full flex items-start gap-3 sm:gap-5 text-left py-3.5 sm:py-4 px-1 sm:px-3"
      >
        <span
          aria-hidden
          className="gfaq-num shrink-0 italic font-normal pt-0.5 sm:pt-1"
          style={{
            fontFamily: 'var(--font-heading, inherit)',
            color: accent,
            fontSize: 'clamp(0.85rem, 0.7rem + 0.3vw, 1rem)',
            letterSpacing: '0.05em',
            minWidth: 28,
          }}
        >
          {num}.
        </span>
        <span
          {...elementProps(sectionId, `items.${itemIndex}.question`, 'heading')}
          className="gfaq-q flex-1 italic leading-snug text-brand-ink/95"
          style={{
            fontFamily: 'var(--font-heading, inherit)',
            fontWeight: 400,
            fontSize: 'clamp(1.05rem, 0.85rem + 0.55vw, 1.35rem)',
            letterSpacing: '-0.005em',
          }}
        >
          {item.question}
        </span>
        <span
          aria-hidden
          className="relative shrink-0 mt-1"
          style={{ width: 22, height: 22 }}
        >
          <span
            className="absolute top-1/2 left-1/2"
            style={{ width: 11, height: 1, background: accent, transform: 'translate(-50%,-50%)' }}
          />
          <span
            className="absolute top-1/2 left-1/2 transition-all duration-500"
            style={{
              width: 1,
              height: 11,
              background: accent,
              transform: open ? 'translate(-50%,-50%) rotate(90deg)' : 'translate(-50%,-50%)',
              opacity: open ? 0 : 1,
              transitionTimingFunction: 'cubic-bezier(0.65,0,0.35,1)',
            }}
          />
        </span>
      </button>
      <div
        className={cn('grid', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
        style={{
          transition: 'grid-template-rows 0.45s cubic-bezier(0.65,0,0.35,1)',
        }}
      >
        <div className="overflow-hidden">
          <div
            {...elementProps(sectionId, `items.${itemIndex}.answer`, 'text')}
            className="font-normal text-brand-ink/55 max-w-[720px] pb-4 sm:pb-5"
            style={{
              fontSize: 'clamp(0.88rem, 0.82rem + 0.12vw, 0.95rem)',
              lineHeight: 1.65,
              paddingLeft: 'calc(28px + 12px)',
            }}
          >
            {lines.map((line, i) => {
              if (line === '') return <span key={i} className="block h-2 sm:h-3" />
              if (line.startsWith('•')) {
                return (
                  <span key={i} className="block">
                    <span style={{ color: accent }}>•</span>
                    {line.slice(1)}
                  </span>
                )
              }
              return <span key={i} className="block">{line}</span>
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════

export function FAQSection({ config }: FAQSectionProps) {
  const content = (config.content ?? {}) as Partial<FAQContent>
  const variant = config.variant ?? 'startup-accordion'
  const { accentColor, textColor: customTextColor, titleSize, textAlign } = config.style
  const items: FAQItem[] = content.items ?? []
  const universe = variant.split('-')[0]
  const layout = variant.replace(`${universe}-`, '')

  // ═══════════════════════════════════════════
  // STARTUP
  // ═══════════════════════════════════════════

  if (universe === 'startup') {
    const accent = accentColor ?? '#6366f1'

    const header = (
      <div className={cn("text-center mb-12 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {content.eyebrow && (
          <span
            className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full border border-zinc-200 text-zinc-600 bg-white shadow-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
            {content.eyebrow}
          </span>
        )}
        {content.title && (
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            className={cn("text-3xl md:text-4xl font-bold text-zinc-900 leading-tight", titleSize && getTitleSizeClass(titleSize))}
            style={customTextColor ? { color: customTextColor } : undefined}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{content.subtitle}</p>}
      </div>
    )

    if (layout === 'grid') {
      const mid = Math.ceil(items.length / 2)
      const left = items.slice(0, mid)
      const right = items.slice(mid)

      return (
        <section className="bg-zinc-50 py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
                <div className="px-6">
                  {left.map((item, i) => (
                    <StartupAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
                <div className="px-6">
                  {right.map((item, i) => (
                    <StartupAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={mid + i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )
    }

    // accordion (default)
    return (
      <section className="bg-zinc-50 py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
            <div className="px-6">
              {items.map((item, i) => (
                <StartupAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // CORPORATE
  // ═══════════════════════════════════════════

  if (universe === 'corporate') {
    const accent = accentColor ?? '#3b82f6'

    const header = (
      <div className={cn("text-center mb-12 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {content.eyebrow && (
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded border text-blue-400 border-blue-500/30"
            style={accentColor ? { color: accentColor, borderColor: `${accentColor}4d` } : undefined}
          >
            {content.eyebrow}
          </span>
        )}
        {content.title && (
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            className={cn("text-3xl md:text-4xl font-bold text-brand-ink leading-tight", titleSize && getTitleSizeClass(titleSize))}
            style={customTextColor ? { color: customTextColor } : undefined}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-slate-400 max-w-2xl mx-auto">{content.subtitle}</p>}
      </div>
    )

    if (layout === 'grid') {
      const mid = Math.ceil(items.length / 2)
      const left = items.slice(0, mid)
      const right = items.slice(mid)

      return (
        <section className="bg-brand-cream py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                <div className="px-6">
                  {left.map((item, i) => (
                    <CorporateAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
                  ))}
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                <div className="px-6">
                  {right.map((item, i) => (
                    <CorporateAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={mid + i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )
    }

    // accordion
    return (
      <section className="bg-brand-cream py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="px-6">
              {items.map((item, i) => (
                <CorporateAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // LUXE
  // ═══════════════════════════════════════════

  if (universe === 'luxe') {
    const accent = accentColor ?? '#CBB68B'

    const header = (
      <div className={cn("text-center mb-14 space-y-5", textAlign && getTextAlignClass(textAlign))}>
        {content.eyebrow && (
          <span
            className="inline-block text-[11px] font-normal tracking-[0.25em] uppercase"
            style={{ color: accent }}
          >
            {content.eyebrow}
          </span>
        )}
        {content.title && (
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            className={cn("text-3xl md:text-4xl font-normal tracking-wide text-zinc-900 leading-tight", titleSize && getTitleSizeClass(titleSize))}
            style={customTextColor ? { color: customTextColor } : undefined}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base font-normal text-zinc-500 max-w-2xl mx-auto tracking-wide">{content.subtitle}</p>}
        <div className="w-16 h-px mx-auto" style={{ backgroundColor: accent }} />
      </div>
    )

    if (layout === 'grid') {
      const mid = Math.ceil(items.length / 2)
      const left = items.slice(0, mid)
      const right = items.slice(mid)

      return (
        <section className="bg-white py-24 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                {left.map((item, i) => (
                  <LuxeAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
                ))}
              </div>
              <div>
                {right.map((item, i) => (
                  <LuxeAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={mid + i} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )
    }

    // accordion
    return (
      <section className="bg-white py-24 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div>
            {items.map((item, i) => (
              <LuxeAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // CREATIVE
  // ═══════════════════════════════════════════

  if (universe === 'creative') {
    const header = (
      <div className={cn("mb-12 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {content.eyebrow && (
          <span className="inline-block text-xs font-black uppercase tracking-tight bg-yellow-300 text-zinc-900 px-3 py-1 border-2 border-zinc-900 rotate-[-1deg]">
            {content.eyebrow}
          </span>
        )}
        {content.title && (
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            className={cn("text-3xl md:text-5xl font-black text-zinc-900 leading-none uppercase tracking-tight", titleSize && getTitleSizeClass(titleSize))}
            style={customTextColor ? { color: customTextColor } : undefined}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-base text-zinc-600 max-w-2xl">{content.subtitle}</p>}
      </div>
    )

    if (layout === 'grid') {
      const mid = Math.ceil(items.length / 2)
      const left = items.slice(0, mid)
      const right = items.slice(mid)

      return (
        <section className="bg-[#f5f0e8] py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-[-2px]">
                {left.map((item, i) => (
                  <CreativeAccordionItem key={item.id} item={item} sectionId={config.id} itemIndex={i} />
                ))}
              </div>
              <div className="space-y-[-2px]">
                {right.map((item, i) => (
                  <CreativeAccordionItem key={item.id} item={item} sectionId={config.id} itemIndex={mid + i} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )
    }

    // accordion
    return (
      <section className="bg-[#f5f0e8] py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-[-2px]">
            {items.map((item, i) => (
              <CreativeAccordionItem key={item.id} item={item} sectionId={config.id} itemIndex={i} />
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // ECOMMERCE
  // ═══════════════════════════════════════════

  if (universe === 'ecommerce') {
    const accent = accentColor ?? '#059669'

    const header = (
      <div className={cn("text-center mb-12 space-y-4", textAlign && getTextAlignClass(textAlign))}>
        {content.eyebrow && (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100"
            style={{ color: accent }}
          >
            {content.eyebrow}
          </span>
        )}
        {content.title && (
          <h2
            {...elementProps(config.id, 'title', 'heading')}
            className={cn("text-3xl md:text-4xl font-bold text-zinc-900 leading-tight", titleSize && getTitleSizeClass(titleSize))}
            style={customTextColor ? { color: customTextColor } : undefined}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-lg text-zinc-500 max-w-2xl mx-auto">{content.subtitle}</p>}
      </div>
    )

    if (layout === 'grid') {
      const mid = Math.ceil(items.length / 2)
      const left = items.slice(0, mid)
      const right = items.slice(mid)

      return (
        <section className="bg-white py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6">
            {header}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                {left.map((item, i) => (
                  <EcommerceAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
                ))}
              </div>
              <div className="space-y-3">
                {right.map((item, i) => (
                  <EcommerceAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={mid + i} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )
    }

    // accordion
    return (
      <section className="bg-white py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
        <div className="max-w-3xl mx-auto px-6">
          {header}
          <div className="space-y-3">
            {items.map((item, i) => (
              <EcommerceAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // BRIXSA
  // ═══════════════════════════════════════════

  if (universe === 'brixsa') {
    return (
      <section
        style={{
          backgroundColor: '#F7EFE1',
          padding: '96px 0',
          overflow: 'hidden',
          fontFamily: "var(--font-body, inherit)",
        }}
      >
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            {content.title && (
              <h2
                {...elementProps(config.id, 'title', 'heading')}
                style={{
                  fontFamily: "var(--font-heading, inherit)",
                  fontSize: '40px',
                  fontWeight: 600,
                  color: customTextColor ?? 'var(--color-background, #e1e1e1)',
                  lineHeight: 1.2,
                  margin: 0,
                  letterSpacing: '-0.01em',
                }}
              >
                {content.title}
              </h2>
            )}
            {content.subtitle && (
              <p
                {...elementProps(config.id, 'subtitle', 'text')}
                style={{
                  fontSize: '16px',
                  color: 'color-mix(in srgb, var(--color-background, #e1e1e1) 50%, transparent)',
                  maxWidth: '540px',
                  margin: '16px auto 0',
                  lineHeight: 1.6,
                }}
              >
                {content.subtitle}
              </p>
            )}
          </div>
          <div>
            {items.map((item, i) => (
              <BrixsaAccordionItem key={item.id} item={item} sectionId={config.id} itemIndex={i} />
            ))}
          </div>
        </div>
      </section>
    )
  }

  // ═══════════════════════════════════════════
  // GLASS (default fallback)
  // ═══════════════════════════════════════════

  {
    const accent = accentColor ?? '#a78bfa'

    const header = (
      <div className={cn("text-center mb-7 sm:mb-10", textAlign && getTextAlignClass(textAlign))}>
        {/* MOBILE uniquement : FAQ resserrée (titre + lignes + questions + numéros plus compacts) */}
        <style dangerouslySetInnerHTML={{ __html: `
          @media (max-width: 767px) {
            .gfaq-title { font-size: 1.75rem !important; margin-bottom: 4px !important; }
            .gfaq-row { padding-top: 10px !important; padding-bottom: 10px !important; gap: 10px !important; }
            .gfaq-q { font-size: 0.95rem !important; line-height: 1.3 !important; }
            .gfaq-num { font-size: 0.78rem !important; min-width: 20px !important; }
          }
        ` }} />
        {content.eyebrow && (
          <span
            className="inline-block font-medium uppercase mb-4 sm:mb-5"
            style={{
              fontFamily: 'var(--font-body, inherit)',
              fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)',
              color: accent,
              letterSpacing: '0.3em',
              borderBottom: `1px solid ${accent}59`,
              padding: '0 14px 6px',
            }}
          >
            {content.eyebrow}
          </span>
        )}
        {content.title && (
          <GrandTitle
            dark={false}
            color={customTextColor ?? '#021734'}
            editProps={elementProps(config.id, 'title', 'heading')}
            className={cn("gfaq-title leading-tight", titleSize && getTitleSizeClass(titleSize))}
          >
            {content.title}
          </GrandTitle>
        )}
        {content.subtitle && <p {...elementProps(config.id, 'subtitle', 'text')} className="text-sm sm:text-base lg:text-lg text-brand-ink/40 max-w-2xl mx-auto mt-3">{content.subtitle}</p>}
      </div>
    )

    if (layout === 'grid') {
      const mid = Math.ceil(items.length / 2)
      const left = items.slice(0, mid)
      const right = items.slice(mid)

      return (
        <section className="bg-brand-cream noise-overlay dot-grid py-20 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)' }}>
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            {header}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-white/[0.06] overflow-hidden">
                <div className="px-6">
                  {left.map((item, i) => (
                    <GlassAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
                  ))}
                </div>
              </div>
              <div className="bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-white/[0.06] overflow-hidden">
                <div className="px-6">
                  {right.map((item, i) => (
                    <GlassAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={mid + i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )
    }

    // accordion
    return (
      <section className="py-10 sm:py-16 overflow-hidden" style={{ fontFamily: 'var(--font-body, inherit)', backgroundColor: '#F7EFE1' }}>
        <div className="max-w-[1160px] mx-auto px-5 sm:px-8 relative">
          {header}
          <div style={{ borderTop: `1px solid ${accent}2e` }}>
            {items.map((item, i) => (
              <GlassAccordionItem key={item.id} item={item} accent={accent} sectionId={config.id} itemIndex={i} />
            ))}
          </div>
        </div>
      </section>
    )
  }
}

export const faqMeta = {
  type: 'faq',
  label: 'FAQ',
  icon: '❓',
  variants: [
    'startup-accordion',
    'startup-grid',
    'corporate-accordion',
    'corporate-grid',
    'luxe-accordion',
    'luxe-grid',
    'creative-accordion',
    'creative-grid',
    'ecommerce-accordion',
    'ecommerce-grid',
    'glass-accordion',
    'glass-grid',
    'brixsa-accordion',
  ],
  defaultVariant: 'startup-accordion',
  defaultContent: {},
}
